<script>
import Loader from "../widgets/loader";
import TableBusyLoader from "../widgets/table-busy-loader";
import _ from "lodash";
import icons from "@/assets/icons";

export default {
  name: "ComponentModel",
  components: {
    Loader,
    TableBusyLoader
  },
  props: {
    sectionTitle: String,
    cardTitle: String,
    loading: { type: Boolean, default: false },
    resourceRoute: String,
    modelVariable: String,
    modelKey: { type: String, default: "id" },
    columns: {
      type: Object,
      default() {
        return {
          [this.modelVariable || "model"]: [
            {
              key: "name",
              label: "Name",
              sortable: true,
              tdClass: "align-middle"
            }
          ]
        };
      }
    },
    cardAttrs: {
      type: Object,
      default() {
        return { title: null, subTitle: null, class: "border-0" };
      }
    },
    showPagination: { type: Boolean, default: true },
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 10 },
    from: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    search: { type: String, default: "" },
    sortBy: { type: String, default: "" },
    sortDesc: { type: Boolean, default: false },
    sortDirection: { type: String, default: "asc" },
    enableActionButtons: { type: Boolean, default: true },
    denyCreate: { type: Boolean, default: false },
    denyExport: { type: Boolean, default: true },
    hideTableHeader: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    modelsPaginated: {
      type: [Array, Function],
      default() {
        return [];
      }
    },
    tableProps: {
      type: Object,
      default() {
        return {};
      }
    },
    tableMinHeight: { type: Number },
    tableHeight: { type: Number, default: 530 },
    filterParams: { type: Object, default: () => {} },
    actions: {
      type: Array,
      default: () => []
    },
    actionButtons: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentPage: this.page,
      localModelsPaginated: [],
      debounceUpdateLocalModelsPaginated: this.lodash.debounce(this.updateLocalModelsPaginated, 250),
      localBusy: false,
      icons
    };
  },
  computed: {
    actionOnLast() {
      const columns = this.columns[this.modelVariable || "model"] || [];
      return columns.findIndex(c => c.key === "action") === columns.length - 1;
    },
    visibleActionButtons() {
      return this.actionButtons.filter(actionButton => !actionButton.hide?.());
    }
  },
  watch: {
    modelsPaginated() {
      if (!(this.modelsPaginated instanceof Function)) return;
      this.updateLocalModelsPaginated();
    },
    search() {
      if (!(this.modelsPaginated instanceof Function)) return;
      this.debounceTableRefresh();
    }
  },
  methods: {
    debounceTableRefresh: _.debounce(function() {
      if (this.$refs.table) this.$refs.table.refresh();
    }, 250),
    setLocalModelsPaginated(items) {
      this.localModelsPaginated = items instanceof Array ? items.slice() : [];
      this.localBusy = false;
    },
    updateLocalModelsPaginated() {
      this.localBusy = true;
      this.$nextTick(() => {
        try {
          // Call provider function passing it the context and optional callback
          const data = this.modelsPaginated({ currentPage: this.page }, this.setLocalModelsPaginated);
          if (data instanceof Promise) {
            // Provider returned Promise
            data.then(items => {
              // Provider resolved with items
              this.setLocalModelsPaginated(items);
            });
          } else if (data instanceof Array) {
            // Provider returned Array data
            this.setLocalModelsPaginated(data);
          } else {
            /* istanbul ignore if */
            if (this.items.length !== 2) {
              this.localBusy = false;
            }
          }
        } catch (e) /* istanbul ignore next */ {
          this.localBusy = false;
        }
      });
    },
    emitCreateClick(event) {
      return this.$emit("create-click", event);
    },
    emitExportClick(event) {
      return this.$emit("export-click", event);
    }
  }
};
</script>

<template>
  <div>
    <h4 class="mb-2 font-size-14" v-if="sectionTitle">{{ sectionTitle }}</h4>
    <b-card
      v-bind="{ bodyClass: 'd-flex flex-column gap-16px', ...cardAttrs }"
      :title="undefined"
      :sub-title="undefined"
    >
      <slot name="card-title">
        <div class="row" v-if="cardTitle">
          <div class="font-size-600 text-neutral-300">
            {{ cardTitle }}
          </div>
        </div>
      </slot>
      <slot name="before-table">
        <div class="row">
          <div class="col-12 col-md-6 mb-2">
            <slot name="card-tools" v-bind="{ denyCreate, emitCreateClick, actions }">
              <div>
                <b-button v-if="!denyCreate" @click="emitCreateClick" variant="primary" size="lg" class="rounded me-2">
                  <span>
                    <component :is="icons.Plus"></component>
                  </span>
                  <span style="font-size: 14px;">
                    {{ $t("Create New") }}
                  </span>
                </b-button>

                <b-button v-if="!denyExport" @click="emitExportClick" variant="success" size="lg" class="rounded me-2">
                  <span style="font-size: 14px;">
                    {{ $t("Export") }}
                  </span>
                </b-button>

                <template v-for="(action, key) in actions">
                  <component :key="key" :is="action.component" v-bind="action" />
                </template>
              </div>
            </slot>
          </div>
          <div v-if="!hideTableHeader" class="d-flex col-12 col-md-6 justify-content-md-end">
            <slot name="table-header">
              <!--          show per table-->
              <div>
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center w-100">
                    <b-form-select
                      class="form-select-sm"
                      :value="perPage"
                      @change="$emit('update:perPage', $event)"
                      size="sm"
                      :options="[10, 50, 100, 250, 1000]"
                    ></b-form-select
                    >&nbsp;
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="w-100">
                <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                  <div class="prefix-icon">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.8374 10.7933L15 13.9552L13.9552 15L10.7933 11.8374C9.61682 12.7805 8.15349 13.2935 6.64566 13.2913C2.97726 13.2913 0 10.3141 0 6.64566C0 2.97726 2.97726 0 6.64566 0C10.3141 0 13.2913 2.97726 13.2913 6.64566C13.2935 8.15349 12.7805 9.61682 11.8374 10.7933ZM10.3562 10.2454C11.2933 9.28169 11.8166 7.98988 11.8145 6.64566C11.8145 3.7895 9.50108 1.47681 6.64566 1.47681C3.7895 1.47681 1.47681 3.7895 1.47681 6.64566C1.47681 9.50108 3.7895 11.8145 6.64566 11.8145C7.98988 11.8166 9.28169 11.2933 10.2454 10.3562L10.3562 10.2454Z"
                        fill="#7E889D"
                      />
                    </svg>
                  </div>

                  <label class="d-inline-flex align-items-center w-100">
                    <b-form-input
                      :value="search"
                      @update="$emit('update:search', $event)"
                      type="search"
                      :placeholder="$t('Pencarian') + '...'"
                      class="form-control form-control-sm ms-2"
                      :debounce="500"
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </slot>
          </div>
        </div>
      </slot>
      <!-- Table -->
      <b-table
        ref="table"
        :busy="busy"
        @update:busy="$emit('update:busy', $event)"
        :items="modelsPaginated"
        sticky-header
        :fields="columns[modelVariable]"
        :current-page="page"
        :per-page="perPage"
        :filter="filterParams"
        :sort-by="sortBy"
        @update:sortBy="$emit('update:sortBy', $event)"
        :sort-desc="sortDesc"
        @update:sortDesc="$emit('update:sortDesc', $event)"
        show-empty
        @filtered="$emit('filtered', $event)"
        :responsive="true"
        head-variant="light"
        thead-class="align-middle"
        :style="{ margin: 0, minHeight: `${tableMinHeight || tableHeight}px`, maxHeight: `${tableHeight}px` }"
        v-bind="tableProps"
        hover
        @row-clicked="!$event._denyShow && $emit('show-click', $event)"
        class="table-model"
      >
        <template #table-colgroup="{ fields }">
          <col v-for="field in fields" :key="field.key" v-bind="field" />
        </template>
        <template #cell(index)="data">
          <b>{{ data.index + from }}</b>
        </template>
        <template #cell(action)="data">
          <b-dropdown
            v-if="!data.item['_denyShow'] || !data.item['_denyEdit'] || !data.item['_denyDelete']"
            class="card-drop"
            variant="white"
            toggle-class="p-0"
            :menu-class="{ 'dropdown-menu-end': actionOnLast }"
          >
            <template v-slot:button-content>
              <i class="mdi mdi-dots-horizontal"></i>
            </template>
            <slot name="action" v-bind="{ data }">
              <b-dropdown-item v-if="!data.item['_denyShow']" @click="$emit('show-click', data.item)">
                {{ $t("Detail") }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item['_denyEdit']" @click="$emit('edit-click', data.item)">
                {{ $t("Edit") }}
              </b-dropdown-item>
              <b-dropdown-item v-if="!data.item['_denyDelete']" @click="$emit('delete-click', data.item)">
                <span class="text-danger">{{ $t("Delete") }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-for="actionButton in visibleActionButtons"
                :key="actionButton.key"
                @click="actionButton.action(data.item)"
                :disabled="actionButton.disable(data.item)"
              >
                {{ actionButton.label }}
              </b-dropdown-item>
            </slot>
          </b-dropdown>
        </template>
        <template #cell()="data">
          <template v-if="data.field.component">
            <component
              :is="data.field.component"
              v-bind="{ item: data.item }"
              v-on="data.field['componentListeners'] || {}"
            ></component>
          </template>
          <template v-else>{{ data.value }}</template>
        </template>
        <template #empty="scope">
          <Loader :loading="busy" no-axios>
            <div
              class="card shadow-none rounded-0"
              style="margin: -0.75rem; background-color: #F5F6F7"
              :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }"
            >
              <div
                style="position: absolute;
                  left: 50%;
                  top: 40%;
                  transform: translate(-50%, -50%);"
              >
                <div class="d-flex justify-content-center">
                  <img
                    src="@/assets/images/report_gmailerrorred_24px.png"
                    alt="not found"
                    style="height: 48px;width: 48px;"
                  />
                </div>
                <p class="table-status-text">{{ lodash.upperFirst($t("search_not_found")) }}</p>
              </div>
            </div>
          </Loader>
        </template>
        <template #emptyfiltered="scope">
          <Loader :loading="busy" no-axios>
            <div
              class="card shadow-none rounded-0"
              style="margin: -0.75rem; background-color: #F5F6F7"
              :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }"
            >
              <div
                style="position: absolute;
                  left: 50%;
                  top: 40%;
                  transform: translate(-50%, -50%);"
              >
                <div class="d-flex justify-content-center">
                  <img
                    src="@/assets/images/report_gmailerrorred_24px.png"
                    alt="not found"
                    style="height: 48px;width: 48px;"
                  />
                </div>
                <p class="table-status-text">{{ lodash.upperFirst($t("search_not_found")) }}</p>
              </div>
            </div>
          </Loader>
        </template>
        <template #table-busy>
          <TableBusyLoader :loading="true" no-axios>
            <div
              class="card shadow-none rounded-0"
              style="margin: -0.75rem; background-color: #F5F6F7"
              :style="{ minHeight: `${tableMinHeight || tableHeight - 45}px`, maxHeight: `${tableHeight - 45}px` }"
            >
              <p
                class="position-absolute table-status-text"
                style="
                  height: 40px;
                  position: absolute;
                  left: 50%;
                  top: 30%;
                  transform: translate(-50%, -50%);
                  "
              >
                {{ lodash.upperFirst($t("fetch_data")) }}
              </p>
            </div>
          </TableBusyLoader>
        </template>
      </b-table>
      <div v-if="total > perPage" class="row">
        <div class="col">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <!-- pagination -->
            <b-pagination
              :key="`pagination-button-${total}-${perPage}`"
              :value="page"
              @change="$emit('update:page', $event)"
              :total-rows="total"
              :per-page="perPage"
              :disabled="busy"
              class="pagination pagination-rounded d-flex"
            ></b-pagination>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<style lang="scss" scoped>
p.table-status-text {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}
a.detail-button,
a.edit-button,
a.delete-button {
  padding: 0 5px;
  color: #343434;
  cursor: pointer;
}

a.detail-button:hover {
  color: #28a745;
}

a.edit-button:hover {
  color: #ffc107;
}

a.delete-button:hover {
  color: #dc3545;
}

::v-deep {
  .dataTables_paginate.paging_simple_numbers {
    margin-top: 0.5rem;
  }

  .dataTables_length select {
    height: 39px;
    // background: #F4F7FA;
    border: 1px solid #dbdce2;
    border-radius: 8px;
    font-weight: 700;
    font-size: 16px;
  }

  .dataTables_filter {
    position: relative;
    input {
      height: 39px;
      padding-left: 34px;
      background: #f4f7fa;
      border: 1px solid #dbdce2;
      border-radius: 8px;
    }
    input::placeholder {
      font-weight: 500;
      font-size: 14px;
      color: #7e889d;
    }
    .prefix-icon {
      position: absolute;
      top: 10px;
      left: 18px;
    }
  }
  .page-item.active {
    button {
      border-radius: 4px !important;
    }
  }

  .table-model {
    thead {
      tr th {
        background: #eef7ff;
      }
    }

    thead {
      tr th {
        background-color: #eef7ff;
        text-align: left;
        font-weight: 600;
        font-size: 12px;
      }

      th:first-child {
        background-color: #eef7ff;
        border-radius: 10px 0 0 10px;
      }

      th:last-child {
        border-radius: 0 10px 10px 0;
      }
    }

    tbody tr td,
    tbody tr td > * {
      font-size: 14px;
    }

    tbody tr {
      outline: none;
    }
  }
}
</style>

<style lang="scss" scoped>
@import "~@/design/app";
::v-deep {
  .table-model {
    @extend .jds-table;
    table {
      @extend .table-zebra;
    }
  }
}

.dataTables_length {
  width: 80px;
}
</style>